/**
 * Customer-friendly navigation blueprint.
 *
 * Chapter 2 of the CX Transformation: replace internal ops terminology with business language.
 * Internal Suntronix roles keep the operational sidebar; customer roles get this simpler map.
 */
import {
  Home, Laptop, UsersRound, Warehouse, PackageOpen, ListChecks, Rocket,
  Undo2, UserPlus, UserMinus, LifeBuoy, Sparkles, BarChart3, CalendarClock,
  Shield, DollarSign, Leaf, FileText, ReceiptText, Award, Trophy, ScrollText,
  Settings, HelpCircle, Repeat, Megaphone, MessageSquare, Store, MapPin, Map as MapIcon,
} from "lucide-react";
import { tenantifyNav } from "@/lib/terminology";

/**
 * Each item can carry `roles` to restrict visibility further.
 * If roles is omitted the item is visible to every customer role that reaches this map.
 */
export const CUSTOMER_NAV = tenantifyNav([
  { section: "" , items: [
    { to: "/", label: "Home", icon: Home, testId: "cx-nav-home" },
    { to: "/portal", label: "Admin Portal", icon: Sparkles, testId: "cx-nav-portal",
      roles: ["customer_global_admin", "customer_it_admin", "customer_hr", "customer_finance", "customer_manager", "customer_procurement", "customer_auditor", "customer_viewer"] },
    { to: "/concierge", label: "What would you like to do?", icon: Sparkles, testId: "cx-nav-concierge" },
    { to: "/marketplace", label: "Marketplace", icon: PackageOpen, testId: "cx-nav-marketplace" },
  ]},
  { section: "My Technology", items: [
    { to: "/assets", label: "Assets", icon: Laptop, testId: "cx-nav-assets" },
    { to: "/employees", label: "Employees", icon: UsersRound, testId: "cx-nav-employees",
      roles: ["customer_global_admin", "customer_it_admin", "customer_hr", "customer_manager"] },
    { to: "/warehouse", label: "Inventory", icon: Warehouse, testId: "cx-nav-inventory",
      roles: ["customer_global_admin", "customer_it_admin"] },
    { to: "/our-stock", label: "Our stock at Suntronix", icon: Warehouse, testId: "cx-nav-our-stock",
      roles: ["customer_global_admin", "customer_it_admin"] },
    { to: "/tenant-home", label: "Store Home", icon: Store, testId: "cx-nav-store-home",
      roles: ["customer_global_admin", "customer_procurement", "customer_finance"] },
    { to: "/tenant-catalogue", label: "Catalogue", icon: PackageOpen, testId: "cx-nav-catalogue",
      roles: ["customer_global_admin", "customer_procurement", "customer_finance"] },
    { to: "/my-quotes", label: "My Quotes", icon: ScrollText, testId: "cx-nav-my-quotes",
      roles: ["customer_global_admin", "customer_procurement", "customer_finance"] },
    { to: "/my-orders", label: "My Orders", icon: PackageOpen, testId: "cx-nav-my-orders",
      roles: ["customer_global_admin", "customer_procurement", "customer_finance"] },
    { to: "/portal/request-hub", label: "Request Hub", icon: PackageOpen, testId: "cx-nav-request-hub", hint: "Pickup, wipe, warehousing, retirement" },
    { to: "/my-tenant-assets", label: "My Assets & Warranty", icon: Laptop, testId: "cx-nav-my-assets",
      roles: ["customer_global_admin", "customer_procurement", "customer_it_admin"] },
    { to: "/my-invoices", label: "My Invoices", icon: ReceiptText, testId: "cx-nav-my-invoices",
      roles: ["customer_global_admin", "customer_finance", "customer_procurement"] },
    { to: "/my-locations", label: "Delivery Addresses", icon: MapPin, testId: "cx-nav-my-locations",
      roles: ["customer_global_admin", "customer_procurement"] },
  ]},
  { section: "Requests", items: [
    { to: "/deploy", label: "Deploy device", icon: Rocket, testId: "cx-nav-deploy",
      roles: ["customer_global_admin", "customer_it_admin", "customer_hr", "customer_manager"] },
    { to: "/pickups", label: "Recover device", icon: Undo2, testId: "cx-nav-recover",
      roles: ["customer_global_admin", "customer_it_admin", "customer_hr"] },
    { to: "/employees", label: "Onboard employee", icon: UserPlus, testId: "cx-nav-onboard",
      roles: ["customer_global_admin", "customer_hr"] },
    { to: "/offboarding", label: "Offboard employee", icon: UserMinus, testId: "cx-nav-offboard",
      roles: ["customer_global_admin", "customer_hr", "customer_it_admin"] },
    { to: "/services", label: "Request support", icon: LifeBuoy, testId: "cx-nav-support" },
    { to: "/catalogue", label: "Other services", icon: Sparkles, testId: "cx-nav-services",
      roles: ["customer_global_admin", "customer_procurement"] },
    { to: "/requests", label: "Track requests", icon: ListChecks, testId: "cx-nav-track" },
  ]},
  { section: "Insights", items: [
    { to: "/executive", label: "Executive view", icon: Trophy, testId: "cx-nav-executive",
      roles: ["customer_global_admin", "customer_finance", "customer_procurement"] },
    { to: "/governance-centre", label: "Governance & QBR", icon: ScrollText, testId: "cx-nav-governance",
      roles: ["customer_global_admin", "customer_it_admin", "customer_finance", "customer_procurement", "customer_auditor"] },
    { to: "/reports", label: "Reports", icon: BarChart3, testId: "cx-nav-reports" },
    { to: "/refresh-engine", label: "Refresh planner", icon: Repeat, testId: "cx-nav-refresh",
      roles: ["customer_global_admin", "customer_it_admin", "customer_procurement", "customer_finance"] },
    { to: "/warranty", label: "Warranty coverage", icon: Shield, testId: "cx-nav-warranty",
      roles: ["customer_global_admin", "customer_it_admin", "customer_procurement", "customer_finance"] },
    { to: "/refresh-forecast", label: "Refresh forecast", icon: CalendarClock, testId: "cx-nav-forecast",
      roles: ["customer_global_admin", "customer_it_admin", "customer_procurement", "customer_finance"] },
  ]},
  { section: "Documents", items: [
    { to: "/audit", label: "Activity trail", icon: ScrollText, testId: "cx-nav-audit",
      roles: ["customer_global_admin", "customer_auditor", "customer_finance"] },
  ]},
  { section: "Change experience", items: [
    { to: "/whats-new", label: "What's new",       icon: Sparkles,       testId: "cx-nav-whats-new" },
    { to: "/roadmap",   label: "Roadmap",          icon: MapIcon,          testId: "cx-nav-roadmap" },
    { to: "/notices",   label: "Advance notices",  icon: Megaphone,      testId: "cx-nav-notices" },
    { to: "/feedback",  label: "Feedback",         icon: MessageSquare,  testId: "cx-nav-feedback" },
  ]},
  { section: "Administration", items: [
    { to: "/companies", label: "Companies", icon: Trophy, testId: "cx-nav-companies",
      roles: ["customer_global_admin"] },
    { to: "/users", label: "Users & roles", icon: Award, testId: "cx-nav-users",
      roles: ["customer_global_admin"] },
    { to: "/imports", label: "Imports", icon: FileText, testId: "cx-nav-imports",
      roles: ["customer_global_admin", "customer_it_admin", "customer_hr"] },
    { to: "/connectors", label: "Data connectors", icon: Settings, testId: "cx-nav-connectors",
      roles: ["customer_global_admin", "customer_it_admin"] },
  ]},
]);

export const EMPLOYEE_SELF_NAV = tenantifyNav([
  { section: "", items: [
    { to: "/my-devices", label: "My devices", icon: Laptop, testId: "cx-self-devices" },
    { to: "/services", label: "Report device issue", icon: LifeBuoy, testId: "cx-self-issue" },
    { to: "/requests", label: "My requests", icon: ListChecks, testId: "cx-self-requests" },
  ]},
]);

/** Prune a nav definition by role. Items without `roles` are always kept. */
export function pruneForRole(groups, role) {
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.roles || it.roles.includes(role)),
    }))
    .filter((g) => g.items.length > 0);
}

// Explicit list — customer_experience is an INTERNAL SUNTRONIX role despite its prefix.
export const CUSTOMER_ROLES = ["customer_global_admin", "customer_it_admin", "customer_procurement", "customer_finance",
  "customer_hr", "customer_manager", "customer_employee", "customer_auditor", "customer_viewer"];
export function isCustomerRole(role) {
  return CUSTOMER_ROLES.includes(role);
}

// Role gate for customer UI (menus AND direct URLs). A path listed in CUSTOMER_NAV with `roles` is only for those roles;
// customer_employee is self-service (EMPLOYEE_SELF_NAV + home + employee hub).
const PATH_ALIASES = { "/invoices": "/my-invoices", "/people": "/employees" };
const NAV_ROLES = (() => {
  const m = new Map();
  CUSTOMER_NAV.forEach((g) => g.items.forEach((it) => {
    const prev = m.get(it.to);
    const roles = it.roles || null;
    m.set(it.to, prev === undefined ? roles : (prev === null || roles === null ? null : [...new Set([...prev, ...roles])]));
  }));
  return m;
})();
const SELF_PATHS = new Set(["/", "/employee-hub", ...EMPLOYEE_SELF_NAV.flatMap((g) => g.items.map((i) => i.to))]);
export function customerPathAllowed(role, path) {
  const base = "/" + String(path || "/").split("?")[0].split("/").filter(Boolean)[0];
  const key = base === "/undefined" ? "/" : (PATH_ALIASES[base] || base);
  if (role === "customer_employee" && NAV_ROLES.has(key)) return SELF_PATHS.has(key) || NAV_ROLES.get(key) === null;
  const roles = NAV_ROLES.get(key);
  return roles === undefined || roles === null || roles.includes(role);
}
